import { useEffect, useMemo } from "react";
import { observer } from "mobx-react-lite";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { Award, TrendingDown } from "lucide-react";

import { useDelayedFlag, useTran } from "@/hooks";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";

import { useEsgDashboardStore } from "../../hooks";

const BenchmarkingChart = observer(() => {
  const { t } = useTran();
  const { benchmarking, loading, ensureData } = useEsgDashboardStore();
  const showSkeleton = useDelayedFlag(loading);

  useEffect(() => {
    ensureData();
  }, [ensureData]);

  const leadingMetrics = useMemo(() => {
    return benchmarking.filter((item) => {
      const yourValue = item["Your Company"];
      const fphValue = item["First Philippine Holdings"];
      const astraValue = item["Astra Group"];
      const lowerIsBetter =
        !item.metric.includes("Training") && !item.metric.includes("Women");
      if (lowerIsBetter) {
        return yourValue < fphValue && yourValue < astraValue;
      }
      return yourValue > fphValue && yourValue > astraValue;
    }).length;
  }, [benchmarking]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="rounded-radius border border-border bg-card p-3 text-xs shadow-sm">
        <p className="font-semibold text-foreground">{label}</p>
        <p className="text-[11px] text-muted-foreground">
          {payload[0]?.payload?.unit}
        </p>
        {payload.map((entry) => (
          <p key={entry.name} style={{ color: entry.color }}>
            {entry.name}: {entry.value?.toLocaleString?.()}
          </p>
        ))}
      </div>
    );
  };

  if (showSkeleton) {
    return (
      <Card className="overflow-hidden">
        <CardHeader className="border-b border-border">
          <Skeleton className="h-5 w-40" />
          <Skeleton className="mt-2 h-3 w-48" />
        </CardHeader>
        <CardContent className="p-6">
          <Skeleton className="h-64 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!benchmarking.length) {
    return null;
  }

  return (
    <Card className="overflow-hidden">
      <CardHeader className="border-b border-border bg-muted/30 pb-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-semibold text-foreground">
              {t("Industry Benchmarking")}
            </p>
            <p className="text-xs text-muted-foreground">
              {t("Comparison with peer companies")}
            </p>
          </div>
          <Badge className="bg-emerald-500 text-white">
            <Award className="mr-1 h-3 w-3" />
            {t("Leading {{count}}/6 Metrics", { count: leadingMetrics })}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="p-6">
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={benchmarking} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" stroke="#cbd5f5" />
            <XAxis type="number" tick={{ fill: "#64748b", fontSize: 11 }} />
            <YAxis
              type="category"
              dataKey="metric"
              tick={{ fill: "#64748b", fontSize: 11 }}
              width={140}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: "11px", paddingTop: "10px" }} />
            <Bar dataKey="Your Company" fill="#10b981" radius={[0, 4, 4, 0]} />
            <Bar
              dataKey="First Philippine Holdings"
              fill="#3b82f6"
              radius={[0, 4, 4, 0]}
            />
            <Bar dataKey="Astra Group" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>

        <div className="mt-4 rounded-radius bg-emerald-500/10 p-3">
          <div className="flex items-start gap-2 text-sm text-emerald-600">
            <TrendingDown className="mt-0.5 h-4 w-4" />
            <div>
              <p className="font-medium">
                {t("Outperforming industry average")}
              </p>
              <p className="text-xs text-emerald-500">
                {t(
                  "Your company leads in {{count}} out of 6 key sustainability metrics.",
                  { count: leadingMetrics },
                )}
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
});

export default BenchmarkingChart;
